/**
 * First-visit guides for every nav surface. When a user lands on a route for
 * the first time, a centralised modal explains what it is, anchors a quote
 * from Jake or Georgina's interview, names what it replaces in their current
 * stack, and offers a hands-on action.
 *
 * Keyed by route. Use `getGuideForPath(pathname)` to match — top-level routes
 * match exactly; parameterised routes (/customer/:id, /studio/:id) match by
 * prefix.
 */

export interface ScreenGuideQuote {
  text: string;
  speaker: 'Georgina' | 'Jake';
}

export interface ScreenGuide {
  /** Route or prefix this guide applies to. */
  match: string;
  /** True if this is a prefix match (used for routes with params). */
  prefix?: boolean;
  /** Display heading. */
  title: string;
  /** One short paragraph: what + why. */
  narration: string;
  /** Pain-point quote from the discovery interviews. */
  quote?: ScreenGuideQuote;
  /** Plain-English description of what this surface replaces. */
  replaces?: string;
  /** Optional "try this" CTA. */
  tryThis?: string;
}

export const SCREEN_GUIDES: ScreenGuide[] = [
  {
    match: '/owner',
    title: 'Owner Dashboard',
    narration:
      "Per-studio P&L with 7-day sparklines, AI cost vs human benchmark, and the alerts panel of high-value leads waiting on a hand. This is your weekly check-in surface.",
    quote: {
      text: "Loving the comms feed. Visibility is night and day from where we were 3 months back.",
      speaker: 'Jake',
    },
    replaces: 'Monthly accountant report + spreadsheet of leakage points.',
    tryThis: 'Hover the 7d trend column — mint sparklines are up, red are down.',
  },
  {
    match: '/network',
    title: 'Network at a glance',
    narration:
      "All 11 studios in one screen. Overnight deposits, missed calls, AI hand-offs and cross-studio routes. The escalations rail on the right is what the network needs YOU on.",
    quote: {
      text: "Every operator is running the risk of: is it Daniel I called two minutes ago, is it Mike from last week?",
      speaker: 'Georgina',
    },
    replaces: 'Walking from studio to studio. Group chats. WhatsApp threads with each manager.',
    tryThis: 'Click an escalation on the right to open that customer record.',
  },
  {
    match: '/studio/',
    prefix: true,
    title: 'Single-studio view',
    narration:
      "The studio's own pulse: today's inquiries, deposit-ready leads, active AI conversations, tomorrow's confirmations. Quick links to its calendar, artists, deposits and missed-call log.",
    replaces: 'Kitomba "today" screen + back-of-house whiteboard.',
    tryThis: 'Top tabs jump you to that studio\'s calendar, artists, or front-desk.',
  },
  {
    match: '/availability',
    title: 'Artist availability heatmap',
    narration:
      "Two-week fortnight grid per artist. Green = booked solid (healthy). Amber = partial gap. Red = wide-open gap your team needs to fill. Click any cell to jump straight into the calendar.",
    quote: {
      text: "Every guy with a number looks the same.",
      speaker: 'Georgina',
    },
    replaces: 'Manually reading each artist\'s calendar and cross-referencing.',
    tryThis: 'Switch the studio filter to "All studios" to see the whole network grouped.',
  },
  {
    match: '/floor',
    title: 'Floor — today live',
    narration:
      "Designed for the iPad on the front desk. Arriving / In Chair / Completed buckets, with check-in → in-chair timer → wrap-up → snap photos → progress → payment → roll-deposit-forward rebooking. Bookings carry their imagery so the artist preps with reference photos.",
    quote: {
      text: "The artist on the chair never sees a chatbot. They see their next booking with the customer's reference photos already attached.",
      speaker: 'Jake',
    },
    replaces: 'Paper booking sheet + Square POS at the counter + manual rebooking phone calls.',
    tryThis: 'Click any in-chair row to walk the check-out flow with the new photo step.',
  },
  {
    match: '/comms',
    title: 'Internal Comms · Slack-equivalent',
    narration:
      "Channels per studio + DMs + threads + emoji + @mentions. Plus an Operations Bot that auto-posts ops events (deposits land, AI hand-offs open, rebookings close) so the network's pulse is always visible.",
    replaces: 'Instagram group chats + WhatsApp + Slack used for an inconsistent subset of conversations.',
    tryThis: 'Open # coburg-ops — see Sarah Holman\'s deposit auto-pinged with a clickable customer chip.',
  },
  {
    match: '/queue',
    title: 'My queue',
    narration:
      "The Agent's hot-leads work list, worked oldest first. Fresh enquiries, callbacks and re-calls surface as they arrive, and you clear a small finite list rather than an endless carousel. Each item carries evidence (weighted reasons) so you know why it's there and what channel the customer prefers.",
    quote: {
      text: "It is just that endless carousel that you're just constantly calling the same people.",
      speaker: 'Georgina',
    },
    replaces: 'Random outbound calling. Cycling through Kitomba customer list with no priority.',
    tryThis: 'Click "Why this matters" on any row to see the AI evidence behind the ranking.',
  },
  {
    match: '/customers',
    title: 'Customers',
    narration:
      "Every record in one searchable list. 51,585 customers — including the dormant book. Filter by stage (qualifying / booked / lapsed / dormant) or engagement (active / warm / lapsed).",
    quote: {
      text: "We have literally 50,000 people on our database and we're getting all these new clients but we're not squeezing the database enough because we can't do both.",
      speaker: 'Jake',
    },
    replaces: 'Kitomba customer list + Excel export + manual segmentation by hand.',
    tryThis: 'Search any name or suburb — every customer is one click away.',
  },
  {
    match: '/customer/',
    prefix: true,
    title: 'Customer record',
    narration:
      "One card per person. Everything you know — source, design intent, bookings, deposits, rapport notes, future-readiness signals, inspiration gallery, conversation thread — in one place. The composer at the bottom sends on any channel.",
    quote: {
      text: "We need to physically put the reference photos in there. The guys need to physically write the thing.",
      speaker: 'Jake',
    },
    replaces: 'Kitomba\'s siloed client card + paper notes + screenshots in Slack.',
    tryThis: 'Scroll the conversation. Click DEPOSIT LINK in the composer to see the live lifecycle.',
  },
  {
    match: '/conversations',
    title: 'Conversations · the shared inbox',
    narration:
      "Every active customer conversation — across SMS, IG DM, Messenger, email and voice — in one Intercom-style queue. Status pills tell you who's handling what: AI on it, awaiting agent, live call, missed.",
    quote: {
      text: "The amount of missed-call opportunities coming back from those four logs would be insane.",
      speaker: 'Georgina',
    },
    replaces: 'Dialpad + Manychat + IG DMs + Gmail + JustCall report tabs.',
    tryThis: 'Use the AI active filter to see who AI is talking to right now.',
  },
  {
    match: '/calendar/',
    prefix: true,
    title: 'Studio calendar',
    narration:
      "Day + week view per studio. Booking cards show customer, artist, session length, design brief and reference photos. Drag-and-drop reschedule. Click any empty slot to create a booking; click any booking to open the customer record with a back-to-calendar breadcrumb.",
    replaces: 'Kitomba + paper diary + manually photographing the booking sheet for each artist.',
    tryThis: 'Click any tall booking card — it shows a thumbnail strip with the customer\'s reference photos.',
  },
  {
    match: '/approvals',
    title: 'No-deposit approvals',
    narration:
      "Bookings an agent wants to take with no deposit land here first. Each reserves the slot but stays HELD until you decide — approve to confirm it with the deposit waived (the customer pays in full on the day), or reject to send it back for a normal deposit link. Every decision is logged with who asked, why, and who resolved it.",
    quote: {
      text: "A no-deposit booking should never just happen quietly — it comes to me, with a reason attached.",
      speaker: 'Georgina',
    },
    replaces: 'Verbal "yeah just book them in" over the phone with no paper trail.',
    tryThis: 'Approve a pending request — the booking flips to confirmed and the slot locks in.',
  },
  {
    match: '/deposits',
    title: 'Deposit ledger',
    narration:
      "Every Stripe payment request, live status, and the path from sent to placed. Filter by studio or status; click any row to jump to the customer record. The AWAITING PLACEMENT chip pulses on deposits that have paid but aren't yet linked to a calendar slot.",
    quote: {
      text: "Me skipping the Excel entry under load.",
      speaker: 'Georgina',
    },
    replaces: 'Manual Excel deposit log entry. Stripe dashboard cross-reference.',
    tryThis: 'Click any PAID row that has no booking — it lifts to the top of your queue with PLACE BOOKING NOW.',
  },
  {
    match: '/confirmations',
    title: 'Tomorrow\'s confirmation messages',
    narration:
      "Automated SMS sequence sent to each booking 24 hours out. Four templated messages: confirm + getting here + prep + closer — auto-filled from the studio's settings (address, parking, transport, eat-beforehand prompts). $0.11 per sequence instead of a front-desker phoning each booking.",
    replaces: 'Front desk phoning each customer. AI voice calls (which cost $1.68 each).',
    tryThis: 'Read the sample sequence on the right — that\'s exactly what the customer gets at 4pm.',
  },
  {
    match: '/future-readiness',
    title: 'Future-Readiness Calendar',
    narration:
      "Every \"maybe in May\" extracted from a call or chat becomes a scheduled re-contact. AI drafts the opener for you; approve and send. The density heatmap shows where future load lands.",
    replaces: 'Sticky notes + memory + hoping someone remembers to follow up.',
    tryThis: 'Tap a day on the heatmap to see who\'s scheduled. Approve any draft to fire it.',
  },
  {
    match: '/artists',
    title: 'Artist management',
    narration:
      "Per-artist rates (hourly + small / half-day / full-day), contact details, working pattern, RDOs, and leave blocks. Toggle weekdays to set the standard roster; add start/end leave blocks when artists are away.",
    replaces: 'Each artist managing their own availability in their head + texting the manager.',
    tryThis: 'Pick an artist on the left to edit their pattern.',
  },
  {
    match: '/reporting',
    title: 'Reporting dashboard',
    narration:
      "Live funnel from Meta ad spend → inquiries → qualified leads → deposits paid → bookings completed, with per-studio and per-campaign breakdowns. The numbers Georgina takes into the weekly call.",
    replaces: 'Manual Excel rollup at end of each week.',
    tryThis: 'Look at the per-campaign panel — see which Meta creative is driving deposits cheapest.',
  },
  {
    match: '/settings',
    title: 'Settings & Brain Schema',
    narration:
      "The configuration layer for owners + Georgina, now fully editable. Studios admin (add, edit or archive any location; drives every studio picker + the confirmation SMS templates), a role permissions matrix (None / View / Edit per capability, including a Financials switch that decides who sees dollar amounts), team management (each member's role + assigned studios), plus the schema explorer, AI prompts and audit trail.",
    replaces: 'Source-of-truth scattered across multiple SaaS admins.',
    tryThis: 'Permissions tab: set a role\'s Financials to None, then act as that persona. Every dollar amount in the app masks to $••••.',
  },
  {
    match: '/leaderboard',
    title: 'Rebook Leaderboard',
    narration:
      "Artists ranked by in-chair rebooks this month. Streaks light up the people who are turning every session into the next one. Launch TV mode for the studio wall — keeps the floor honest at a glance.",
    quote: {
      text: "If they walk out without the next session booked, we've left money on the table. Make it visible.",
      speaker: 'Jake',
    },
    replaces: 'Whiteboard tallies in the back room + Monday-morning manager guesswork.',
    tryThis: 'Filter to one studio, then hit Launch TV mode — that\'s what mounts on the wall.',
  },
];

/** Match a pathname to its guide. Tries exact match first, then prefix matches. */
export function getGuideForPath(pathname: string): ScreenGuide | undefined {
  // Exact match first.
  const exact = SCREEN_GUIDES.find((g) => !g.prefix && g.match === pathname);
  if (exact) return exact;
  // Prefix match (longest first so /customer/:id wins over /).
  const prefix = SCREEN_GUIDES.filter((g) => g.prefix && pathname.startsWith(g.match)).sort(
    (a, b) => b.match.length - a.match.length,
  );
  return prefix[0];
}

/** Stable key for tracking "seen" state. Prefix routes collapse to their pattern. */
export function guideKey(guide: ScreenGuide): string {
  return guide.match;
}
